import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { checkinAction } from 'actions/workflow';
import { TextPhone, Email } from 'components/Icons';
import { BackButton, Button } from 'components/Button';
import { FormRow } from 'components/Form';
import GeneralError from 'components/GeneralError';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { SEND_TEXT_COOLDOWN_SECONDS } from 'utils/constants';
import Alert from 'components/Alert';

const ExistingApplication = ({
  navigate,
  dispatch,
  searchParams,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const [isSendingText, setIsSendingText] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(SEND_TEXT_COOLDOWN_SECONDS);
  const [generalError, setGeneralError] = useState(null);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const mobileNumber = get(checkinData, 'mobileNumber');
  const email = get(checkinData, 'email');
  const showBackButton = !get(checkinData, 'hideBack');

  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const resendText = (e) => {
    e.preventDefault();
    setIsSendingText(true);

    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: {
        resendText: true,
      },
      success: () => {
        setIsSendingText(false);
        setCooldownSeconds(SEND_TEXT_COOLDOWN_SECONDS);
      },
      fail: handleGeneralFail,
    }));
  };

  const handleGeneralFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSendingText(false);
  }

  return (
    <>
      <h2 className="relative -top-4 text-xl md:text-2xl text-center font-semibold text-slate-800 leading-tight mb-2">
        Welcome Back, {get(checkinData, 'firstName')}!
      </h2>
      <div className="flex flex-col items-center text-center gap-8 relative">
        <Alert size="sm" variant="success" className="w-full px-4 py-2">
          We found an existing application for you. Pick up right where you left off.
        </Alert>

        <p className="text-slate-800">
          To keep your information secure, we've sent a link to continue your application to both of the following:
        </p>

        <div className="w-full flex flex-col gap-4">
          <div className="flex items-center gap-4 bg-blue-50/45 border border-blue-100 rounded-lg p-4">
            <TextPhone size={48} />
            <div className="text-left">
              <p className="text-xs mb-0.5 font-semibold text-gray-500 uppercase tracking-wide">Text Message</p>
              <p className="text-gray-800 font-semibold">{mobileNumber}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 bg-blue-50/45 border border-blue-100 rounded-lg p-4">
            <Email size={48} />
            <div className="text-left">
              <p className="text-xs mb-0.5 font-semibold text-gray-500 uppercase tracking-wide">Email</p>
              <p className="text-gray-800 font-semibold">{email}</p>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500">
          Didn't receive anything? Check your spam folder or tap below to resend.
        </p>

        <GeneralError error={generalError} />

        <FormRow className="w-full flex-col-reverse">
          {showBackButton ? (
            <BackButton
              className="flex-1"
              onBeforeBack={() => setGeneralError(null)}
              onFail={handleGeneralFail}
            />
          ) : null}
          <Button
            className="flex-1"
            label={cooldownSeconds > 0 ? `Resend link (${cooldownSeconds}s)` : 'Resend link'}
            isLoading={isSendingText}
            onClick={resendText}
            isDisabled={cooldownSeconds > 0}
          />
        </FormRow>
      </div>
    </>
  );
};

ExistingApplication.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default ExistingApplication;
